import { getTotalPrice } from '@shared/lib/money';
import { createSelector } from 'reselect';

import { AviaPassengerType, SelectedSeatFragment } from '@websky/graphql';

import { getState } from '../rootSelector';

import { getSelectedSeats, getSelectedSeatsWithPricesBySelectedSegmentId } from '../selectedSeats/selectors';
import { getSelectedPassengerId } from '../selectedPassengerId/selectors';
import { getSelectedSegmentId } from '../selectedSegmentId/selectors';
import { getSeatMapSegments } from '../seatMap/selectors';

import { PassengerListItem, PassengersRecommendedSeats } from '../types';

export const getPassengersState = createSelector(getState, state => state.passengers);

export const getPassengersSelectedSeats = createSelector(getPassengersState, passengers => {
	const selectedSeats: (SelectedSeatFragment & { passengerId: number })[] = [];

	passengers.forEach(passenger => {
		passenger.selectedSeats.forEach(seat => {
			selectedSeats.push({ ...seat, passengerId: passenger.id });
		});
	});

	return selectedSeats;
});

export const getPassengersList = createSelector(
	getPassengersState,
	getSelectedPassengerId,
	getSelectedSeatsWithPricesBySelectedSegmentId,
	(passengers, selectedPassengerId, selectedSeatsWithPricesBySelectedSegmentId): PassengerListItem[] => {
		return passengers.map(passenger => ({
			id: passenger.id,
			fullName: passenger.fullName,
			isSelected: passenger.id === selectedPassengerId,
			type: passenger.type,
			activeDoubleSeat: passenger.activeDoubleSeat,
			supplierTravellerType: passenger.supplierTravellerType,
			selectedSeats: selectedSeatsWithPricesBySelectedSegmentId[passenger.id] ?? [],
			seatsPrice: getTotalPrice(selectedSeatsWithPricesBySelectedSegmentId[passenger.id], seat => seat.price),
			isDisabled: passenger.type === AviaPassengerType.INF
		}));
	}
);

export const getAvaliablePassengersList = createSelector(getPassengersList, passengers =>
	passengers.filter(passenger => !passenger.isDisabled)
);

export const getSelectedPassenger = createSelector(
	getPassengersList,
	(passengers): PassengerListItem => passengers.find(passenger => passenger.isSelected)
);

export const getIsActiveDoubleSeat = createSelector(
	getSelectedPassenger,
	selectedPassenger => selectedPassenger.activeDoubleSeat
);

export const getPassengersRecommendedSeats = createSelector(getPassengersState, passengers => {
	let recommendedSeats: PassengersRecommendedSeats = {};
	passengers.forEach(passenger => {
		passenger.preselectedSeats.forEach(seat => {
			const travellerRecommendedSeat = {
				segmentId: +seat.segment.id,
				passengerId: +passenger.id,
				row: +seat.seat.row,
				letter: seat.seat.letter,
				price: seat.seat.price,
				comfort: seat.seat.seatService?.comfort
			};

			recommendedSeats = {
				...recommendedSeats,
				[+seat.segment.id]: {
					...(recommendedSeats?.[+seat.segment.id] || []),
					[+passenger.id]: [
						...(recommendedSeats?.[+seat.segment.id]?.[+passenger.id] || []),
						travellerRecommendedSeat
					]
				}
			};
		});
	});

	return recommendedSeats;
});

export const getPassengersRecommendedSeatsBySelectedSegmentId = createSelector(
	getPassengersRecommendedSeats,
	getSelectedSegmentId,
	(recommendedSeats, selectedSegmentId) => recommendedSeats[selectedSegmentId] ?? {}
);

export const getIsAllSeatsSelected = createSelector(
	getSelectedSeats,
	getAvaliablePassengersList,
	getSeatMapSegments,
	(selectedSeats, passengers, seatMapSegments) => {
		return Object.values(seatMapSegments).every((segment, segmentId) => {
			if (segment.isSeatsAvailableInCheckinOnly || !segment.availableSeatsCount) {
				return true;
			}

			return (
				selectedSeats.filter(selectedSeat => selectedSeat.segmentId === segmentId).length === passengers.length
			);
		});
	}
);
